/**
 * Stage 1: AI Content Generation
 * Interface for generating course content using Claude AI
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseDraft } from '../../../types/creator-workflow';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import './Stage1Generation.css';

interface Stage1GenerationProps {
  draft: CourseDraft | null;
  onCreateDraft: (data: any) => Promise<CourseDraft | null>;
  onGenerateContent: (data: any, draftId?: string) => Promise<void>;
  onAdvanceStage: () => Promise<void>;
  onStartSentenceEditing: () => Promise<void>;
  /** @deprecated kept for legacy fallback only */
  onParseContent: () => Promise<void>;
  loading: boolean;
}

const Stage1Generation: React.FC<Stage1GenerationProps> = ({
  draft,
  onCreateDraft,
  onGenerateContent,
  onAdvanceStage,
  onStartSentenceEditing,
  onParseContent,
  loading,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  // 從 localStorage 恢復表單數據
  const getInitialFormData = () => {
    const backup = localStorage.getItem('stage1_form_backup');
    if (backup) {
      try {
        console.log('✅ 從 localStorage 恢復 Stage 1 表單數據');
        const parsed = JSON.parse(backup);
        // Ensure all fields have default values to avoid uncontrolled to controlled warning
        return {
          cefr_level: parsed.cefr_level || 'B1',
          content_type: parsed.content_type || 'dialogue',
          target_word_count: parsed.target_word_count || 300,
          topic: parsed.topic || '',
          scenario: parsed.scenario || '',
          custom_requirements: parsed.custom_requirements || {},
        };
      } catch (err) {
        console.error('Failed to parse Stage 1 backup:', err);
      }
    }
    return {
      cefr_level: 'B1',
      content_type: 'dialogue',
      target_word_count: 300,
      topic: '',
      scenario: '',
      custom_requirements: {},
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Custom requirements state (array of key-value pairs for UI)
  const [customReqPairs, setCustomReqPairs] = useState<Array<{ key: string; value: string }>>(() => {
    const reqs = getInitialFormData().custom_requirements;
    if (reqs && typeof reqs === 'object' && Object.keys(reqs).length > 0) {
      return Object.entries(reqs).map(([key, value]) => ({ key, value: String(value) }));
    }
    return [];
  });

  // 編輯內容的 state
  const [editedContent, setEditedContent] = useState(draft?.generated_text || '');
  const [isEditing, setIsEditing] = useState(false);
  const [contentConfirmed, setContentConfirmed] = useState(false);

  // 從 JSON 中提取對話內容的輔助函數
  const extractDialogueFromJSON = (input: string | any): string => {
    console.log('🔍 extractDialogueFromJSON 輸入類型:', typeof input);
    console.log('🔍 extractDialogueFromJSON 輸入:', typeof input === 'string' ? input.substring(0, 200) : input);

    // 如果輸入是字串，先嘗試移除 markdown code blocks
    if (typeof input === 'string') {
      // 移除 markdown code blocks (```json ... ``` 或 ``` ... ```)
      const codeBlockMatch = input.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
      if (codeBlockMatch) {
        console.log('📝 偵測到 markdown code block，正在移除...');
        input = codeBlockMatch[1].trim();
      }

      try {
        JSON.parse(input);
        // 可以解析,繼續處理
      } catch {
        // 不是JSON,直接返回
        console.log('✅ 輸入已經是純文字,直接使用');
        return input;
      }
    }

    try {
      // 如果輸入已經是物件,直接使用
      let parsed = input;
      if (typeof input === 'string') {
        parsed = JSON.parse(input);
      }

      console.log('🔍 JSON 解析成功, 結構:', Array.isArray(parsed) ? 'Array' : Object.keys(parsed));

      // 如果是完整 JSON 格式,提取 dialogue 或 article 欄位
      if (parsed.dialogue) {
        console.log('✅ 從 JSON 中提取 dialogue 內容');
        return parsed.dialogue;
      }

      if (parsed.article) {
        console.log('✅ 從 JSON 中提取 article 內容');
        const article = parsed.article;

        // 如果 article 是物件,轉換成純文字
        if (typeof article === 'object' && article !== null) {
          console.log('🔄 將 article 物件轉換成純文字');
          let text = '';

          // 添加英文標題
          if (article.title_en) {
            text += article.title_en + '\n\n';
          }

          // 添加中文標題
          if (article.title_zh) {
            text += article.title_zh + '\n\n';
          }

          // 添加段落 (paragraphs 是物件陣列,每個有 text_en 和 text_zh)
          if (article.paragraphs && Array.isArray(article.paragraphs)) {
            const paragraphTexts = article.paragraphs.map((p: any) => {
              let paragraphText = '';
              if (p.text_en) paragraphText += p.text_en;
              if (p.text_zh) paragraphText += '\n' + p.text_zh;
              return paragraphText;
            });
            text += paragraphTexts.join('\n\n');
          }

          return text;
        }

        // 如果 article 本身就是字串,直接返回
        return article;
      }

      // 如果已經是純文字,直接返回
      console.warn('⚠️ JSON 中沒有 dialogue/article 欄位, 使用原始內容');
      return typeof input === 'string' ? input : JSON.stringify(input);
    } catch (err) {
      // 如果不是 JSON,直接返回原始內容
      console.warn('⚠️ generated_text 不是 JSON 格式,使用原始內容', err);
      return typeof input === 'string' ? input : JSON.stringify(input);
    }
  };

  // 當 draft.generated_text 變化時,更新 editedContent
  useEffect(() => {
    if (draft?.generated_text) {
      // 提取對話內容 (從 JSON 中提取 dialogue 欄位)
      const dialogueContent = extractDialogueFromJSON(draft.generated_text);
      setEditedContent(dialogueContent);
      // 檢查 localStorage 是否有編輯中的內容
      const editBackup = draft.id ? localStorage.getItem(`stage1_edit_backup_${draft.id}`) : null;
      if (editBackup) {
        try {
          const { content, confirmed } = JSON.parse(editBackup);
          // 也對 localStorage 的內容進行提取(可能是舊的 JSON 格式)
          const extractedContent = extractDialogueFromJSON(content);
          console.log('🔍 localStorage 恢復的內容類型:', typeof content);
          console.log('🔍 提取後的內容類型:', typeof extractedContent);
          console.log('🔍 提取後的內容:', typeof extractedContent === 'string' ? extractedContent.substring(0, 100) : extractedContent);
          setEditedContent(extractedContent);
          setContentConfirmed(confirmed || false);
          console.log('✅ 從 localStorage 恢復 Stage 2 編輯內容');
        } catch (err) {
          console.error('Failed to parse edit backup:', err);
        }
      }
    }
  }, [draft?.generated_text, draft?.id]);

  // 每次表單數據變化時保存
  useEffect(() => {
    localStorage.setItem('stage1_form_backup', JSON.stringify(formData));
  }, [formData]);

  // 保存編輯中的內容到 localStorage
  useEffect(() => {
    if (draft?.id && editedContent) {
      localStorage.setItem(
        `stage1_edit_backup_${draft.id}`,
        JSON.stringify({ content: editedContent, confirmed: contentConfirmed })
      );
    }
  }, [editedContent, contentConfirmed, draft?.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? Number(value) : value;
    const newFormData = { ...formData, [name]: newValue };
    setFormData(newFormData);
    // 立即保存
    localStorage.setItem('stage1_form_backup', JSON.stringify(newFormData));
  };

  // Custom requirements management
  const addCustomRequirement = () => {
    setCustomReqPairs([...customReqPairs, { key: '', value: '' }]);
  };

  const updateCustomRequirement = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...customReqPairs];
    updated[index][field] = newValue;
    setCustomReqPairs(updated);

    // Convert to object and update formData
    const reqObject = updated.reduce((acc, pair) => {
      if (pair.key.trim()) {
        acc[pair.key.trim()] = pair.value;
      }
      return acc;
    }, {} as Record<string, any>);

    const newFormData = { ...formData, custom_requirements: reqObject };
    setFormData(newFormData);
    localStorage.setItem('stage1_form_backup', JSON.stringify(newFormData));
  };

  const removeCustomRequirement = (index: number) => {
    const updated = customReqPairs.filter((_, i) => i !== index);
    setCustomReqPairs(updated);

    // Convert to object and update formData
    const reqObject = updated.reduce((acc, pair) => {
      if (pair.key.trim()) {
        acc[pair.key.trim()] = pair.value;
      }
      return acc;
    }, {} as Record<string, any>);

    const newFormData = { ...formData, custom_requirements: reqObject };
    setFormData(newFormData);
    localStorage.setItem('stage1_form_backup', JSON.stringify(newFormData));
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Convert custom requirements to object
    const customReqObject = customReqPairs.reduce((acc, pair) => {
      if (pair.key.trim()) {
        acc[pair.key.trim()] = pair.value;
      }
      return acc;
    }, {} as Record<string, any>);

    // Prepare generate data according to backend API requirements
    const generateData = {
      cefr_level: formData.cefr_level,
      content_type: formData.content_type,
      target_word_count: formData.target_word_count,
      topic: formData.topic,
    };

    // Add optional fields only if they have values
    if (formData.scenario && formData.scenario.trim()) {
      (generateData as any).scenario = formData.scenario;
    }

    if (Object.keys(customReqObject).length > 0) {
      (generateData as any).custom_requirements = customReqObject;
    }

    // If draft doesn't exist, create it first, then generate content
    if (!draft) {
      console.log('📝 Creating new draft with parameters...');
      const newDraft = await onCreateDraft({
        cefr_level: formData.cefr_level,
        content_type: formData.content_type,
        target_word_count: formData.target_word_count,
        title: `${formData.content_type} - ${formData.cefr_level}`,
        description: formData.topic || 'Auto-generated course',
        tags: [],
      });

      if (newDraft?.id) {
        console.log('✅ Draft created successfully! Generating content with draft ID:', newDraft.id);
        await onGenerateContent(generateData, newDraft.id);
      } else {
        console.error('❌ Failed to create draft');
      }
      return;
    }

    console.log('🚀 Generating content with data:', generateData);
    await onGenerateContent(generateData);
  };

  const handleRegenerateClick = async () => {
    // 清空表單數據並重置狀態
    setFormData({
      cefr_level: draft?.cefr_level || 'B1',
      content_type: draft?.content_type || 'dialogue',
      target_word_count: draft?.target_word_count || 300,
      topic: '',
      scenario: '',
      custom_requirements: {},
    });
    setContentConfirmed(false);
    setIsEditing(false);

    // 清空 custom requirements pairs
    setCustomReqPairs([]);

    // 清空已生成的內容，讓表單重新顯示
    setEditedContent('');

    // 清除 localStorage 的備份數據
    localStorage.removeItem('stage1_form_backup');
    if (draft?.id) {
      localStorage.removeItem(`stage1_edit_backup_${draft.id}`);
    }

    console.log('✅ Ready to regenerate content with new parameters');
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!draft?.id) {
      console.error('Cannot save edit: draft or draft.id is null');
      return;
    }

    // 調用 API 更新 draft 的 generated_text
    try {
      // 這裡需要調用 updateDraft API (假設已在 CreatorWizardPage 中)
      // 暫時只保存到 localStorage
      localStorage.setItem(
        `stage1_edit_backup_${draft.id}`,
        JSON.stringify({ content: editedContent, confirmed: false })
      );
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save edit:', error);
    }
  };

  const handleConfirmContent = () => {
    if (!draft?.id) {
      console.error('Cannot confirm content: draft or draft.id is null');
      return;
    }

    setContentConfirmed(true);
    setIsEditing(false);
    localStorage.setItem(
      `stage1_edit_backup_${draft.id}`,
      JSON.stringify({ content: editedContent, confirmed: true })
    );
    // UI updates via setContentConfirmed(true) above
  };

  // 只有當本地編輯內容存在時才顯示生成內容（這樣 regenerate 時清空 editedContent 就能顯示表單）
  const hasGenerated = editedContent && editedContent.length > 0;

  // Check if user uploaded materials (Path A-F)
  const hasUploadedMaterials = draft?.material_source && draft.material_source !== 'ai_generated';

  // For uploaded materials path, show preview and proceed to parsing
  if (hasUploadedMaterials) {
    return (
      <div className="stage-1-generation">
        <header className="stage-header">
          <h2>{t('creator:workflow.stage1.generatedContent')}</h2>
          <p>{t('creator:workflow.stage1.pleaseReview')}</p>
        </header>

        <div className="uploaded-materials-section">
          <div className="material-source-badge">
            {draft.material_source?.replace(/_/g, ' ').toUpperCase()}
          </div>

          {/* Show uploaded English text */}
          {draft.uploaded_text_en && (
            <div className="form-section">
              <h3>{t('creator:workflow.contentEditor.englishText')}</h3>
              <div className="content-preview">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {draft.uploaded_text_en}
                </pre>
              </div>
              <div className="text-stats">
                {t('creator:workflow.stage0.wordsCount')} {draft.uploaded_text_en.split(/\s+/).filter(w => w).length} |
                {t('creator:workflow.stage0.charsCount')} {draft.uploaded_text_en.length}
              </div>
            </div>
          )}

          {/* Show uploaded Chinese text */}
          {draft.uploaded_translation && (
            <div className="form-section">
              <h3>{t('creator:workflow.contentEditor.chineseTranslation')}</h3>
              <div className="content-preview">
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {draft.uploaded_translation}
                </pre>
              </div>
              <div className="text-stats">
                {t('creator:workflow.stage0.charsCount')} {draft.uploaded_translation.length}
              </div>
            </div>
          )}

          {/* Show uploaded audio */}
          {draft.uploaded_audio_url && (
            <div className="form-section">
              <h3>{t('creator:workflow.stage0.audioUpload')}</h3>
              <audio controls src={resolveMediaUrl(draft.uploaded_audio_url)} style={{ width: '100%' }}>
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          <div className="info-box" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#e8f4fd', borderRadius: '8px' }}>
            <p><strong>{t('creator:workflow.stage1.pleaseReview')}</strong></p>
          </div>

          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button
              onClick={async () => {
                if (!draft?.id) {
                  console.error('Cannot proceed: draft or draft.id is null');
                  return;
                }

                try {
                  await onStartSentenceEditing();
                } catch (error) {
                  console.error('Failed during navigation:', error);
                }
              }}
              className="btn-primary"
              disabled={loading}
            >
              {loading ? t('common:processing') : t('common:next')}
            </button>
          </div>
        </div>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>{t('creator:workflow.stage0.loadingOverlay')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="stage-1-generation">
      <header className="stage-header">
        <h2>{t('creator:workflow.stage1.title')}</h2>
        <p>
          {loading
            ? t('creator:workflow.stage1.aiGenerating')
            : t('creator:workflow.stage1.description')}
        </p>
      </header>

      {!hasGenerated && (
        <form className="generation-form" onSubmit={handleGenerate}>
          <div className="form-section">
            <h3>{t('creator:workflow.stage1.generateContent')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cefr_level">
                  {t('creator:workflow.stage1.cefrRequired')}
                </label>
                <select
                  id="cefr_level"
                  name="cefr_level"
                  value={formData.cefr_level}
                  onChange={handleInputChange}
                  required
                >
                  <option value="A1">A1</option>
                  <option value="A2">A2</option>
                  <option value="B1">B1</option>
                  <option value="B2">B2</option>
                  <option value="C1">C1</option>
                  <option value="C2">C2</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="content_type">
                  {t('creator:workflow.stage1.contentTypeLabel')}
                </label>
                <select
                  id="content_type"
                  name="content_type"
                  value={formData.content_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="dialogue">{t('creator:workflow.stage1.contentTypeDialogue')}</option>
                  <option value="article">{t('creator:workflow.stage1.contentTypeArticle')}</option>
                  <option value="story">{t('creator:workflow.stage1.contentTypeStory')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="target_word_count">
                {t('creator:workflow.stage1.wordCountLabel')}
              </label>
              <input
                type="number"
                id="target_word_count"
                name="target_word_count"
                value={formData.target_word_count}
                onChange={handleInputChange}
                min={50}
                max={1000}
                step={50}
                required
              />
              <span className="help-text">
                {t('creator:workflow.stage1.wordCountHint')}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="topic">
                {t('creator:workflow.stage1.topicLabel')}
              </label>
              <input
                type="text"
                id="topic"
                name="topic"
                value={formData.topic}
                onChange={handleInputChange}
                placeholder={t('creator:workflow.stage1.topicPlaceholder')}
                required
                maxLength={200}
              />
            </div>

            <div className="form-group">
              <label htmlFor="scenario">
                {t('creator:workflow.stage1.scenarioLabel')}
              </label>
              <input
                type="text"
                id="scenario"
                name="scenario"
                value={formData.scenario}
                onChange={handleInputChange}
                placeholder={t('creator:workflow.stage1.topicPlaceholder')}
                maxLength={300}
              />
            </div>

            <div className="form-group">
              <label>
                {t('creator:workflow.stage1.customRequirementsLabel')}
              </label>

              {customReqPairs.map((pair, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={t('creator:workflow.stage1.customReqKey')}
                    value={pair.key}
                    onChange={(e) => updateCustomRequirement(index, 'key', e.target.value)}
                    style={{ flex: '1', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    maxLength={50}
                  />
                  <span style={{ color: '#666' }}>:</span>
                  <input
                    type="text"
                    placeholder={t('creator:workflow.stage1.customReqValue')}
                    value={pair.value}
                    onChange={(e) => updateCustomRequirement(index, 'value', e.target.value)}
                    style={{ flex: '2', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    maxLength={200}
                  />
                  <button
                    type="button"
                    onClick={() => removeCustomRequirement(index)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={addCustomRequirement}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginTop: '0.5rem',
                }}
              >
                {t('creator:workflow.stage1.addRequirement')}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !formData.topic}
            >
              {loading ? t('creator:workflow.stage1.generatingAI') : t('creator:workflow.stage1.generateContent')}
            </button>
          </div>
        </form>
      )}

      {hasGenerated && (
        <div className="generated-content">
          <div className="content-header">
            <h3>{t('creator:workflow.stage1.generatedContent')}</h3>
            <button
              onClick={handleRegenerateClick}
              className="btn-secondary"
              disabled={loading}
            >
              {t('creator:workflow.stage1.regenerate')}
            </button>
          </div>

          <div className="content-stats">
            <div className="stat-item">
              <span className="stat-label">{t('creator:workflow.stage0.wordsCount')}</span>
              <span className="stat-value">{draft?.actual_word_count || 'N/A'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('creator:workflow.stage1.cefrRequired')}</span>
              <span className="stat-value">{draft?.cefr_level || 'N/A'}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('creator:workflow.stage1.generateContent')}</span>
              <span className="stat-value">{draft?.content_type || 'N/A'}</span>
            </div>
          </div>

          <div className="content-editor">
            <div className="editor-header">
              <h4>{t('creator:workflow.stage1.pleaseReview')}</h4>
              {contentConfirmed ? (
                <span className="confirmed-badge">{t('creator:workflow.stage1.contentConfirmed')}</span>
              ) : (
                <span className="pending-badge">{t('creator:workflow.stage1.pleaseReview')}</span>
              )}
            </div>
            <textarea
              className="content-textarea"
              value={editedContent}
              onChange={handleContentChange}
              rows={20}
              placeholder={t('creator:workflow.stage1.generatedContent')}
              style={{
                width: '100%',
                padding: '1rem',
                fontFamily: 'monospace',
                fontSize: '14px',
                lineHeight: '1.6',
                border: '1px solid #ddd',
                borderRadius: '4px',
                resize: 'vertical',
              }}
            />
            <div className="editor-info">
              <span className="char-count">
                {t('creator:workflow.stage0.charsCount')} {editedContent.length} | {t('creator:workflow.stage0.wordsCount')} {editedContent.split(/\s+/).filter(w => w).length}
              </span>
              {isEditing && (
                <span className="editing-indicator" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                  {t('common:unsavedChanges')}
                </span>
              )}
            </div>
          </div>

          {draft?.stage_progress?.generation?.completed && (
            <div className="success-box">
              <p>{t('creator:workflow.stage1.contentConfirmed')}</p>
            </div>
          )}

          <div className="form-actions">
            {isEditing && (
              <button
                onClick={handleSaveEdit}
                className="btn-secondary"
                disabled={loading}
              >
                {t('common:save')}
              </button>
            )}
            {!contentConfirmed && (
              <button
                onClick={handleConfirmContent}
                className="btn-primary"
                disabled={loading || isEditing}
                title={isEditing ? t('common:unsavedChanges') : t('common:confirm')}
              >
                {t('common:confirm')}
              </button>
            )}
            {contentConfirmed && (
              <button
                onClick={async () => {
                  if (!draft?.id) {
                    console.error('Cannot proceed: draft or draft.id is null');
                    alert('錯誤：無法取得課程 ID');
                    return;
                  }

                  try {
                    console.log('🔄 Starting sentence segmentation (review boundaries before analysis)...');
                    await onStartSentenceEditing();
                    console.log('✅ Sentence editing stage ready.');
                  } catch (error) {
                    console.error('❌ Failed during navigation:', error);
                    alert('導航失敗，請稍後再試');
                  }
                }}
                className="btn-primary"
                disabled={loading}
                title={t('common:next')}
              >
                {loading ? t('common:processing') : t('common:next')}
              </button>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('creator:workflow.stage1.aiGenerating')}</p>
          <span className="loading-hint">
            {t('common:processing')}
          </span>
        </div>
      )}
    </div>
  );
};

export default Stage1Generation;
